// services/gamificationService.js
import {
  getFirestore,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  increment
} from 'firebase/firestore'
import { app } from '../firebase'
import UserService from './UserService'

const db = getFirestore(app)

// Badge-Definitionen (synchron mit GamificationBadge.js halten)
const BADGES = [
  { id: 'first_contribution', requirement: 1, points: 100 },
  { id: 'helpful_tech', requirement: 5, points: 250 },
  { id: 'knowledge_expert', requirement: 10, points: 500 },
  { id: 'master_tech', requirement: 25, points: 1000 },
  { id: 'legend', requirement: 50, points: 2500 }
]

// Rank-System basierend auf Level
const RANKS = [
  { level: 1, name: 'Neuling', minPoints: 0 },
  { level: 2, name: 'Hilfreicher Techniker', minPoints: 250 },
  { level: 3, name: 'Wissensexperte', minPoints: 750 },
  { level: 4, name: 'Meister-Techniker', minPoints: 2000 },
  { level: 5, name: 'Legende', minPoints: 5000 }
]

class GamificationService {
  // User Stats laden
  async getUserStats (userEmail, userId) {
    if (!userEmail) {
      console.warn('getUserStats called with no userEmail.')
      // Gib einen Zustand zurück, der einem nicht existierenden User entspricht
      return {
        totalContributions: 0,
        approvedContributions: 0,
        pendingContributions: 0,
        points: 0,
        level: 1,
        rank: 'Neuling',
        unlockedBadges: []
      }
    }
    try {
      // Get user's companyId
      const companyId = await UserService.getUserCompanyId(userId)

      const userStatsRef = doc(
        db,
        'companies',
        companyId,
        'userGamification',
        userEmail
      )
      const docSnap = await getDoc(userStatsRef)

      if (docSnap.exists()) {
        const data = docSnap.data()
        return {
          totalContributions: data.totalContributions || 0,
          approvedContributions: data.approvedContributions || 0,
          pendingContributions: data.pendingContributions || 0,
          points: data.points || 0,
          level: data.level || 1,
          rank: data.rank || 'Neuling',
          unlockedBadges: data.unlockedBadges || [],
          lastContributionDate: data.lastContributionDate
        }
      } else {
        // Initialisiere neuen User
        const initialStats = {
          totalContributions: 0,
          approvedContributions: 0,
          pendingContributions: 0,
          points: 0,
          level: 1,
          rank: 'Neuling',
          unlockedBadges: [],
          createdAt: new Date().toISOString()
        }
        await setDoc(userStatsRef, initialStats)
        return initialStats
      }
    } catch (error) {
      console.error('Error loading user stats:', error)
      throw error
    }
  }

  // Nach erfolgreicher Submission aufrufen
  async recordContribution (userEmail, userId) {
    try {
      // Get user's companyId
      const companyId = await UserService.getUserCompanyId(userId)

      const userStatsRef = doc(
        db,
        'companies',
        companyId,
        'userGamification',
        userEmail
      )

      // ✨ STEP 1: Ensure document exists first
      const docSnap = await getDoc(userStatsRef)
      if (!docSnap.exists()) {
        // Create initial document for new user
        await setDoc(userStatsRef, {
          totalContributions: 0,
          approvedContributions: 0,
          pendingContributions: 0,
          points: 0,
          level: 1,
          rank: 'Neuling',
          unlockedBadges: [],
          createdAt: new Date().toISOString()
        })
      }

      // ✨ STEP 2: Atomic increment für base contribution + points
      // Das verhindert Race Conditions bei schnellen aufeinanderfolgenden Submissions
      await updateDoc(userStatsRef, {
        totalContributions: increment(1),
        pendingContributions: increment(1),
        points: increment(50), // Base points für Contribution
        lastContributionDate: new Date().toISOString()
      })

      // STEP 3: Jetzt Stats neu laden um aktuelle Werte zu haben
      const currentStats = await this.getUserStats(userEmail, userId)

      // STEP 4: Prüfe ob neue Badges freigeschaltet wurden
      const newBadges = BADGES.filter(
        badge =>
          currentStats.totalContributions >= badge.requirement &&
          !currentStats.unlockedBadges.includes(badge.id)
      )

      // STEP 5: Wenn neue Badges → füge Badge-Punkte hinzu
      if (newBadges.length > 0) {
        let badgePoints = 0
        const newUnlockedBadges = [...currentStats.unlockedBadges]

        newBadges.forEach(badge => {
          badgePoints += badge.points
          newUnlockedBadges.push(badge.id)
        })

        const totalPointsAfterBadges = currentStats.points + badgePoints

        // Berechne neuen Level/Rank mit Badge-Punkten
        const newRank = this.calculateRank(totalPointsAfterBadges)

        // ✨ Atomic update für Badge-Punkte + Level/Rank
        await updateDoc(userStatsRef, {
          points: increment(badgePoints),
          level: newRank.level,
          rank: newRank.name,
          unlockedBadges: newUnlockedBadges
        })

        // Gib neue Badges zurück für Notification
        return {
          newBadges,
          updatedStats: {
            totalContributions: currentStats.totalContributions,
            points: totalPointsAfterBadges,
            level: newRank.level,
            rank: newRank.name
          }
        }
      } else {
        // Keine neuen Badges → prüfe trotzdem Rank (falls Level-Schwelle überschritten)
        const newRank = this.calculateRank(currentStats.points)

        if (
          newRank.level !== currentStats.level ||
          newRank.name !== currentStats.rank
        ) {
          await updateDoc(userStatsRef, {
            level: newRank.level,
            rank: newRank.name
          })
        }

        return {
          newBadges: [],
          updatedStats: {
            totalContributions: currentStats.totalContributions,
            points: currentStats.points,
            level: newRank.level,
            rank: newRank.name
          }
        }
      }
    } catch (error) {
      console.error('Error recording contribution:', error)
      throw error
    }
  }

  // Wenn Beitrag approved wird
  async approveContribution (userEmail, userId) {
    try {
      // Get user's companyId
      const companyId = await UserService.getUserCompanyId(userId)

      const userStatsRef = doc(
        db,
        'companies',
        companyId,
        'userGamification',
        userEmail
      )

      await updateDoc(userStatsRef, {
        approvedContributions: increment(1),
        pendingContributions: increment(-1),
        points: increment(25) // Bonus für Approval
      })
    } catch (error) {
      console.error('Error approving contribution:', error)
      throw error
    }
  }

  // Wenn Beitrag rejected wird
  async rejectContribution (userEmail, userId) {
    try {
      // Get user's companyId
      const companyId = await UserService.getUserCompanyId(userId)

      const userStatsRef = doc(
        db,
        'companies',
        companyId,
        'userGamification',
        userEmail
      )

      await updateDoc(userStatsRef, {
        totalContributions: increment(-1),
        pendingContributions: increment(-1),
        points: increment(-50) // Punkte zurückziehen
      })
    } catch (error) {
      console.error('Error rejecting contribution:', error)
      throw error
    }
  }

  // Berechne Rank basierend auf Punkten
  calculateRank (points) {
    for (let i = RANKS.length - 1; i >= 0; i--) {
      if (points >= RANKS[i].minPoints) {
        return RANKS[i]
      }
    }
    return RANKS[0]
  }

  // Leaderboard abrufen
  async getLeaderboard (userId, limitParam = 10) {
    // Umbenannt, um Konflikt mit import zu vermeiden
    try {
      // Get user's companyId
      const companyId = await UserService.getUserCompanyId(userId)

      // KORREKT: 'limit' statt 'limitQuery' importieren
      const { collection, query, orderBy, getDocs, limit } = await import(
        'firebase/firestore'
      )

      const leaderboardRef = collection(
        db,
        'companies',
        companyId,
        'userGamification'
      )

      // KORREKT: 'limit()' Funktion verwenden
      const q = query(
        leaderboardRef,
        orderBy('points', 'desc'),
        limit(limitParam) // Den Parameter hier übergeben
      )

      const querySnapshot = await getDocs(q)
      const leaderboard = []

      querySnapshot.forEach(doc => {
        leaderboard.push({
          email: doc.id,
          ...doc.data()
        })
      })

      return leaderboard
    } catch (error) {
      console.error('Error loading leaderboard:', error)
      throw error
    }
  }

  // User Ranking Position
  async getUserRanking (userEmail, userId) {
    try {
      // Get user's companyId
      const companyId = await UserService.getUserCompanyId(userId)

      const { collection, query, getDocs, orderBy } = await import(
        'firebase/firestore'
      )

      // Hole alle Users sortiert nach Punkten
      const allUsersRef = collection(
        db,
        'companies',
        companyId,
        'userGamification'
      )
      const q = query(allUsersRef, orderBy('points', 'desc'))

      const querySnapshot = await getDocs(q)

      // Finde die Position des aktuellen Users in der sortierten Liste
      let ranking = 1
      for (const doc of querySnapshot.docs) {
        if (doc.id === userEmail) {
          return ranking
        }
        ranking++
      }

      // Falls User nicht gefunden wurde
      return null
    } catch (error) {
      console.error('Error getting user ranking:', error)
      return null
    }
  }
}

export default new GamificationService()
